import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { StagesComponent } from '../../../../../../common/stages/stages.component';
import { stages } from '../../opinion-on-issuing-a-decision.routes';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApplicantAppEntityApplicantWrite,
  ApplicationAppEntityApplicationRead,
  EntityTypeAppEntityDictionaryEntityType,
  PatchApplicantApplicantItemRequestParams
} from '../../../../../../openapi';
import {
  selectApplication,
  selectEntityTypes, selectLoading,
  selectPending
} from '../../../../../../store/selectors/application.selectors';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { TabViewComponent } from '../../../../../../common/tab-view/tab-view.component';
import { loadEntityTypes, updateApplicants } from '../../../../../../store/actions/application.actions';
import { SpinnerComponent } from '../../../../../../common/spinner/spinner.component';
import { OpinionOnIssuingADecisionInvestorItemForm } from './forms/opinion-on-issuing-a-decision-investor-item.form';
import { AccordionGroupComponent } from '../../../../../../common/accordion-group/accordion-group.component';
import { AccordionItemComponent } from '../../../../../../common/accordion-item/accordion-item.component';
import { AlertComponent } from '../../../../../../common/alert/alert.component';
import { EntityLabelPipe } from '../../../../../../pipes/applicant-label.pipe';
import { CommonModule } from '@angular/common';
import {
  OpinionOnIssuingADecisionInvestorItemPreviewComponent
} from './components/opinion-on-issuing-a-decision-investor-item-preview/opinion-on-issuing-a-decision-investor-item-preview.component';
import { TabPanelComponent } from '../../../../../../common/tab-view/tab-panel.component';
import {
  OpinionOnIssuingADecisionInvestorNaturalPersonFormComponent
} from './components/opinion-on-issuing-a-decision-investor-natural-person-form/opinion-on-issuing-a-decision-investor-natural-person-form.component';
import {
  OpinionOnIssuingADecisionInvestorCompanyFormComponent
} from './components/opinion-on-issuing-a-decision-investor-company-form/opinion-on-issuing-a-decision-investor-company-form.component';

@Component({
  selector: 'app-opinion-on-issuing-a-decision-investor-data',
  standalone: true,
  imports: [
    StagesComponent,
    SpinnerComponent,
    AccordionGroupComponent,
    AccordionItemComponent,
    AlertComponent,
    EntityLabelPipe,
    CommonModule,
    TabPanelComponent,
    TabViewComponent,
    OpinionOnIssuingADecisionInvestorItemPreviewComponent,
    OpinionOnIssuingADecisionInvestorNaturalPersonFormComponent,
    OpinionOnIssuingADecisionInvestorCompanyFormComponent
  ],
  providers: [OpinionOnIssuingADecisionInvestorItemForm],
  templateUrl: './opinion-on-issuing-a-decision-investor-data.component.html'
})
export class OpinionOnIssuingADecisionInvestorDataComponent implements OnInit, OnDestroy {

  private route: ActivatedRoute = inject(ActivatedRoute);

  private router: Router = inject(Router);

  private store$: Store = inject(Store);

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public stages: string[] = stages;

  public investorForm: OpinionOnIssuingADecisionInvestorItemForm = inject(OpinionOnIssuingADecisionInvestorItemForm);

  public showItemForm = false;

  public investors: WritableSignal<PatchApplicantApplicantItemRequestParams[]> = signal([]);

  public entityTypes: Signal<EntityTypeAppEntityDictionaryEntityType[]> = this.store$.selectSignal(selectEntityTypes);

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  private applicationApplicants: Signal<PatchApplicantApplicantItemRequestParams[]> = computed(() => {
    const app = this.application();
    return app?.applicants?.map(applicant => {
      const v: PatchApplicantApplicantItemRequestParams = {
        id: '',
        applicant: {
          application: app?.uuid,
          entityType: applicant.entityType?.id || '',
          firstName: applicant?.firstName || '',
          lastName: applicant?.lastName || '',
          name: applicant?.name || '',
          pesel: applicant?.pesel || '',
          nip: applicant?.nip || '',
          city: applicant.city,
          email: applicant.email,
          apartmentNumber: applicant.apartmentNumber,
          houseNumber: applicant.houseNumber,
          phoneNumber: applicant.phoneNumber,
          phoneNumber2: applicant.phoneNumber2,
          postalCode: applicant.postalCode,
          postOffice: applicant.postOffice,
          street: applicant.street
        }
      };
      return v;
    }) || [];
  });

  private fetchApplicants: Subscription = toObservable(this.applicationApplicants).subscribe(v => {
    this.investors.set(v);
  });

  public currentIndex: WritableSignal<number | undefined> = signal(undefined);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  @ViewChild('tabViewComponent', { static: false })
  tabViewComponent: TabViewComponent | undefined;

  public onAdd(): void {
    this.showItemForm = true;
    this.currentIndex.set(undefined);
  }

  public onCancel(): void {
    this.router.navigate(['../dane-wniosku'], { relativeTo: this.route });
  }

  public submitAndGoNext(): void {
    if (this.investors().length) {
      this.store$.dispatch(updateApplicants({
        applicationId: this.applicationId || '',
        application: {},
        applicants: this.investors(),
        successFnc: (applicationId) => {
          this.router.navigate(
            ['../inwestor'],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    }
  }

  public onUpdateInvestors(applicant: ApplicantAppEntityApplicantWrite): void {
    if (this.currentIndex() !== undefined) {
      this.investors.update(v => {
        v[this.currentIndex() || 0] = { applicant, id: v[this.currentIndex() || 0].id };
        return v;
      });
      this.currentIndex.set(undefined);
    } else {
      this.investors.update(v => ([...v, { applicant, id: '' }]));
    }
    this.showItemForm = false;
    this.investorForm.reset();
  }

  public onRemove(i: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.investors.update(v => v.filter((_, index) => index != i));
  }

  public onEditInvestor(event: Event, i: number): void {
    const applicant: ApplicantAppEntityApplicantWrite | undefined = this.investors().at(i)?.applicant;
    const tabName: string = this.entityTypes()?.filter(v => v.id === applicant?.entityType)[0]?.name || '';

    event.preventDefault();
    event.stopPropagation();

    if (applicant && tabName) {
      this.currentIndex.set(i);
      this.showItemForm = true;

      setTimeout(() => {

        this.tabViewComponent?.onNavigate(this.tabViewComponent?.panels.filter(v => v?.name === tabName)[0]);
        this.tabViewComponent?.ref.detectChanges();

        this.investorForm.patchValue({
          ...applicant,
          zipCode: applicant?.postalCode,
          buildingNumber: applicant?.houseNumber,
          phone: applicant?.phoneNumber,
          phone2: applicant?.phoneNumber2
        });
      });
    }
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadEntityTypes());
  }

  public ngOnDestroy(): void {
    this.fetchApplicants.unsubscribe();
  }

}
